import { useReducer } from 'react'
import { toast } from 'react-toastify'
import { pick } from 'ramda'
import { useMutation } from '@apollo/client/react'
// Components
import Footer from '@/components/newFooter/newFooter'
import CustomerReview from '@/components/search/CustomerReview'
import Button from '@/components/newButton/Button'
import Options from './Options'
import ToastModal from '../breeder/toastModal'
import QuizResult from '../puppyQuizResult'
// Utils
import { getToken, getUserData } from '@/utils/tokenStorage'
import { SAVE_CUSTOMER_BREEDS_QUIZ } from '@/graphql/index'
import styles from './puppyQuiz.module.scss'

const actions = {
  SET_INITIAL: 'SET_INITIAL',
  PREV_STEP: 'PREV_STEP',
  NEXT_STEP: 'NEXT_STEP',
  SET_HAS_OWNED_DOG: 'SET_HAS_OWNED_DOG',
  SET_LIVE_QUARTERS: 'SET_LIVE_QUARTERS',
  SET_DOG_SIZE: 'SET_DOG_SIZE',
  SET_ALLERGIC: 'SET_ALLERGIC',
  SET_KID_FRIENDLY: 'SET_KID_FRIENDLY',
  SET_TIME_SPENT: 'SET_TIME_SPENT',
  SET_ENERGETIC_LEVEL: 'SET_ENERGETIC_LEVEL',
  SET_FETCH: 'SET_FETCH',
}

const initialState = {
  step: 0,
  fetch: false,
  name: '',
  hasOwnedDog: null,
  liveQuarters: null,
  dogSize: null,
  allergic: null,
  kidFriendly: null,
  timeSpent: null,
  energeticLevel: null,
}

const reducer = (state, action) => {
  switch (action.type) {
    case actions.SET_INITIAL:
      return {
        ...initialState,
      }
    case actions.PREV_STEP:
      return {
        ...state,
        step: state.step - 1,
      }
    case actions.NEXT_STEP:
      return {
        ...state,
        step: state.step + 1,
      }
    case actions.SET_HAS_OWNED_DOG:
      return {
        ...state,
        hasOwnedDog: action.payload,
      }
    case actions.SET_LIVE_QUARTERS:
      return {
        ...state,
        liveQuarters: action.payload,
      }
    case actions.SET_DOG_SIZE:
      return {
        ...state,
        dogSize: action.payload,
      }
    case actions.SET_ALLERGIC:
      return {
        ...state,
        allergic: action.payload,
      }
    case actions.SET_KID_FRIENDLY:
      return {
        ...state,
        kidFriendly: action.payload,
      }
    case actions.SET_TIME_SPENT:
      return {
        ...state,
        timeSpent: action.payload,
      }
    case actions.SET_ENERGETIC_LEVEL:
      return {
        ...state,
        energeticLevel: action.payload,
      }
    case actions.SET_FETCH:
      return {
        ...state,
        fetch: true,
      }

    default:
      throw new Error('Undefined Action')
  }
}

const redenderSteps = (state, dispatch) => {
  const nextStep = () => {
    dispatch({ type: actions.NEXT_STEP })
    toast.dismiss()
  }

  const prevStep = () => dispatch({ type: actions.PREV_STEP })

  const responseAlert = () =>
    toast(
      <ToastModal
        theme="error"
        title="Error"
        message="Please provide a response to proceed"
        buttonTitle="Try Again"
      />
    )

  const steps = [
    <div key={0}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>1</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step1} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          1. Have you owned or raised a dog before?
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: true, label: 'Yes, I’m an experienced dog owner' },
              { value: false, label: 'No, this will be my first time' },
            ]}
            value={state.hasOwnedDog}
            onChange={(value) =>
              dispatch({ type: actions.SET_HAS_OWNED_DOG, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.hasOwnedDog === null}
          title="Next"
          onClick={() =>
            state.hasOwnedDog !== null ? nextStep() : responseAlert()
          }
        />
      </div>
    </div>,
    <div key={1}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>2</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step2} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          2. Where will your new puppy live?
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'low', label: 'An apartment' },
              { value: 'high', label: 'A house with a yard' },
              { value: 'medium', label: 'A house without a yard' },
            ]}
            value={state.liveQuarters}
            onChange={(value) =>
              dispatch({ type: actions.SET_LIVE_QUARTERS, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.liveQuarters === null}
          title="Next"
          onClick={() =>
            state.liveQuarters !== null ? nextStep() : responseAlert()
          }
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
    <div key={2}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>3</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step3} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          3. How big do you want your fully grown dog to be?
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'XS', label: 'Extra Small (<10lb)' },
              { value: 'S', label: 'Small (10-20lb)' },
              { value: 'M', label: 'Medium (20-40lb)' },
              { value: 'L', label: 'Large (40+lb)' },
            ]}
            value={state.dogSize}
            onChange={(value) =>
              dispatch({ type: actions.SET_DOG_SIZE, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.dogSize === null}
          title="Next"
          onClick={() =>
            state.dogSize !== null ? nextStep() : responseAlert()
          }
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
    <div key={3}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>4</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step4} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          4. Is shedding a concern for you, or is anyone in your household
          allergic to fur?{' '}
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'high', label: 'Yes, shedding is a concern for me' },
              {
                value: 'medium',
                label: 'Low-shedding is preferred but not necessary',
              },
              { value: 'low', label: 'This is not a concern for me' },
            ]}
            value={state.allergic}
            onChange={(value) =>
              dispatch({ type: actions.SET_ALLERGIC, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.allergic === null}
          title="Next"
          onClick={() =>
            state.allergic !== null ? nextStep() : responseAlert()
          }
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
    <div key={4}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>5</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step5} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          5. Are you looking for a dog that is good with children?{' '}
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'high', label: 'Yes, that is a must-have' },
              { value: 'medium', label: 'That is preferred but not necessary' },
              { value: 'low', label: 'That is not a concern for me' },
            ]}
            value={state.kidFriendly}
            onChange={(value) =>
              dispatch({ type: actions.SET_KID_FRIENDLY, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.kidFriendly === null}
          title="Next"
          onClick={() =>
            state.kidFriendly !== null ? nextStep() : responseAlert()
          }
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
    <div key={5}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>6</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step6} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          6. How much time do you plan to spend with your dog?
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'high', label: 'A lot - Iʼm mostly at home' },
              { value: 'medium', label: 'Moderate - Iʼm in and out' },
              { value: 'low', label: 'Little - Iʼm usually not home' },
            ]}
            value={state.timeSpent}
            onChange={(value) =>
              dispatch({ type: actions.SET_TIME_SPENT, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.timeSpent === null}
          title="Next"
          onClick={() =>
            state.timeSpent !== null ? nextStep() : responseAlert()
          }
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
    <div key={6}>
      <div className={styles.statusBar}>
        <div className={styles.header}>
          <div className={styles.numbers}>
            <strong>7</strong> of 7
          </div>{' '}
          <div className={styles.text}>Breed Match Quiz</div>
        </div>
        <div className={styles.line}>
          <div className={styles.step7} />
        </div>
      </div>
      <div className={styles.step}>
        <div className={styles.question}>
          7. How energetic do you want your dog to be?
        </div>
        <div className={styles.formHolder}>
          <Options
            options={[
              { value: 'high', label: 'Very - I want an active dog' },
              {
                value: 'medium',
                label: 'Moderate - I like walks and occasional play',
              },
              { value: 'low', label: 'Low - Iʼm looking for a cuddle buddy' },
            ]}
            value={state.energeticLevel}
            onChange={(value) =>
              dispatch({ type: actions.SET_ENERGETIC_LEVEL, payload: value })
            }
          />
        </div>
      </div>
      <div className={styles.sectionBtn}>
        <Button
          theme={['pink-solid', 'large']}
          disabled={state.energeticLevel === null}
          title="View Results"
          onClick={() => {
            dispatch({ type: actions.SET_FETCH })
            window.scrollTo(0, 0)
          }}
        />
        <Button theme={['black', 'large']} title="Back" onClick={prevStep} />
      </div>
    </div>,
  ]
  return steps[state.step]
}

export default function PuppyQuiz() {
  const [state, dispatch] = useReducer(reducer, initialState)
  const userData = getUserData(getToken())
  const [saveQuizOptions, { called }] = useMutation(SAVE_CUSTOMER_BREEDS_QUIZ)

  if (state.fetch && userData && userData.type === 'Customer' && !called) {
    // Save data of this quiz
    saveQuizOptions({
      variables: {
        quiz: pick(
          [
            'hasOwnedDog',
            'liveQuarters',
            'dogSize',
            'allergic',
            'kidFriendly',
            'timeSpent',
            'energeticLevel',
          ],
          state
        ),
      },
    })
  }

  if (state.fetch) {
    return (
      <>
        <div className={styles.resultHolder}>
          <div className={styles.resultInner}>
            <div className={styles.container}>
              <div className={styles.resultTitle}>
                Here are your top matches!
              </div>
              <QuizResult
                data={pick(
                  [
                    'hasOwnedDog',
                    'liveQuarters',
                    'dogSize',
                    'allergic',
                    'kidFriendly',
                    'timeSpent',
                    'energeticLevel',
                  ],
                  state
                )}
                onRetake={() => {
                  dispatch({ type: actions.SET_INITIAL })
                  window.scrollTo(0, 0)
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.customerReview}>
          <CustomerReview />
        </div>
        <Footer />
      </>
    )
  }

  return (
    <div className={styles.content}>
      <div className={styles.container}>{redenderSteps(state, dispatch)}</div>
    </div>
  )
}
